import React, { useEffect, useMemo, useRef, useState } from "react";
import OuterHeader from "../../components/OuterHeader";
import Footer from "../../components/Footer";
import HelprrGroundingOrb from "../../components/HelprrGroundingOrb";
import HelprrBodyExercise from "../../components/HelprrBodyExercise";
import HelprrSensoryGrounding from "../../components/HelprrSensoryGrounding";
import {
  RURAL_LIBRARY_SECTIONS,
  RURAL_PRINTABLE_RESOURCES,
  RURAL_SUPPORT_OPTIONS,
  RURAL_SUPPORT_RESOURCES,
  RURAL_TOOLKIT_BY_FOCUS,
} from "../../data/ruralWellnessData";
import "../../css2/RuralWellness.css";

const CHECKIN_STORAGE_KEY = "helprr:rural-wellness:checkins:v1";

const MOODS = [
  { value: "great", label: "Great", emoji: "😄" },
  { value: "good", label: "Good", emoji: "🙂" },
  { value: "okay", label: "Okay", emoji: "😐" },
  { value: "struggling", label: "Struggling", emoji: "😟" },
  { value: "hard", label: "Very hard", emoji: "😢" },
];

function readSavedCheckIns() {
  if (typeof window === "undefined") return [];

  try {
    const saved = JSON.parse(window.localStorage.getItem(CHECKIN_STORAGE_KEY));
    return Array.isArray(saved) ? saved : [];
  } catch (error) {
    return [];
  }
}

function ResourceDirectory({ resources, compact = false }) {
  const visibleResources = compact ? resources.slice(0, 3) : resources;

  return (
    <div className={`rw-resource-grid ${compact ? "rw-resource-grid--compact" : ""}`}>
      {visibleResources.map((resource) => (
        <article
          key={resource.id}
          className={`rw-resource-card ${resource.featured ? "rw-resource-card--featured" : ""}`}
        >
          <div>
            <span className="rw-resource-category">{resource.category}</span>
            <h3>{resource.title}</h3>
            <p>{resource.description}</p>
          </div>

          <div className="rw-resource-actions">
            {resource.actions.map((action) => {
              const opensNewWindow = action.href.startsWith("http");

              return (
                <a
                  key={action.label}
                  href={action.href}
                  target={opensNewWindow ? "_blank" : undefined}
                  rel={opensNewWindow ? "noreferrer" : undefined}
                >
                  {action.label}
                </a>
              );
            })}
          </div>
        </article>
      ))}
    </div>
  );
}

function ReflectionCard({ title, prompts = [], actions = [] }) {
  const [selectedAction, setSelectedAction] = useState("");

  return (
    <article className="rw-guided-card">
      <span className="rw-card-kicker">Reflect, then choose</span>
      <h3>{title}</h3>

      <div className="rw-prompt-list">
        {prompts.map((prompt, index) => (
          <div key={prompt} className="rw-prompt-row">
            <span>{index + 1}</span>
            <p>{prompt}</p>
          </div>
        ))}
      </div>

      {actions.length > 0 && (
        <div className="rw-action-picker">
          <p>Pick one realistic next step:</p>
          <div className="rw-action-options">
            {actions.map((action) => (
              <button
                type="button"
                key={action}
                className={selectedAction === action ? "is-selected" : ""}
                onClick={() => setSelectedAction(action)}
                aria-pressed={selectedAction === action}
              >
                {action}
              </button>
            ))}
          </div>

          {selectedAction && (
            <p className="rw-selected-action" aria-live="polite">
              Your next step: <strong>{selectedAction}</strong>
            </p>
          )}
        </div>
      )}
    </article>
  );
}

function ConnectionCard({ title, message, alternatives = [], onNotice }) {
  const copyMessage = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      onNotice("Message copied.");
    } catch (error) {
      onNotice("Copy was unavailable. Select the message text instead.");
    }
  };

  return (
    <article className="rw-guided-card">
      <span className="rw-card-kicker">Low-pressure connection</span>
      <h3>{title}</h3>

      <div className="rw-message-box">
        <p>{message}</p>
        <button type="button" onClick={() => copyMessage(message)}>
          Copy message
        </button>
      </div>

      <details className="rw-inline-details">
        <summary>See other ways to say it</summary>
        <div>
          {alternatives.map((alternative) => (
            <button
              type="button"
              key={alternative}
              onClick={() => copyMessage(alternative)}
            >
              <span>{alternative}</span>
              <strong>Copy</strong>
            </button>
          ))}
        </div>
      </details>
    </article>
  );
}

function ConversationCard({ title, helpful = [], avoid = [] }) {
  return (
    <article className="rw-guided-card">
      <span className="rw-card-kicker">Conversation guide</span>
      <h3>{title}</h3>

      <div className="rw-conversation-grid">
        <div className="rw-conversation-column rw-conversation-column--helpful">
          <h4>Helpful language</h4>
          <ul>
            {helpful.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
        </div>

        <div className="rw-conversation-column rw-conversation-column--avoid">
          <h4>Avoid minimizing</h4>
          <ul>
            {avoid.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
        </div>
      </div>
    </article>
  );
}

function SafetyCard({ title, content }) {
  return (
    <article className="rw-safety-card">
      <span aria-hidden="true">!</span>
      <div>
        <h3>{title}</h3>
        <p>{content}</p>
        <div className="rw-safety-actions">
          <a href="tel:988">Call 9-8-8</a>
          <a href="sms:988">Text 9-8-8</a>
        </div>
      </div>
    </article>
  );
}

function BodyBattery() {
  const [battery, setBattery] = useState(55);

  const guidance = useMemo(() => {
    if (battery <= 10) {
      return {
        label: "Very low capacity",
        text: "Reduce demands where possible. Focus on immediate basics and contact a trusted person or support service.",
      };
    }

    if (battery <= 40) {
      return {
        label: "Low capacity",
        text: "Choose one essential task, delay what can wait, and protect a real recovery period.",
      };
    }

    if (battery <= 70) {
      return {
        label: "Managing with limited reserve",
        text: "You are functioning, but your buffer is smaller. Add a short pause before the next demanding task.",
      };
    }

    return {
      label: "More capacity available",
      text: "Protect your energy through pacing rather than spending all of it at once.",
    };
  }, [battery]);

  return (
    <section className="rw-section rw-battery-section" aria-labelledby="rw-battery-title">
      <div className="rw-section-heading">
        <div>
          <span className="rw-eyebrow">Daily capacity</span>
          <h2 id="rw-battery-title">Where is your body battery?</h2>
          <p>
            This is not a score to improve. It is a quick way to match today’s demands to your actual capacity.
          </p>
        </div>
      </div>

      <div className="rw-battery-layout">
        <div className="rw-battery-control">
          <label htmlFor="rw-battery-range">
            Move the slider to your current energy level.
          </label>
          <input
            id="rw-battery-range"
            type="range"
            min="0"
            max="100"
            value={battery}
            onChange={(event) => setBattery(Number(event.target.value))}
          />
          <div className="rw-battery-scale" aria-hidden="true">
            <span>Running on fumes</span>
            <span>More capacity</span>
          </div>
        </div>

        <div className="rw-battery-result" aria-live="polite">
          <div>
            <strong>{battery}%</strong>
            <span>{guidance.label}</span>
          </div>
          <p>{guidance.text}</p>
        </div>
      </div>
    </section>
  );
}

function WeeklyCheckIn({ onNotice }) {
  const [isOpen, setIsOpen] = useState(false);
  const [savedCheckIns, setSavedCheckIns] = useState(readSavedCheckIns);
  const [form, setForm] = useState({
    mood: "",
    stress: 5,
    wentWell: "",
    challenge: "",
    nextStep: "",
    connection: "",
  });

  useEffect(() => {
    if (typeof window === "undefined") return;
    window.localStorage.setItem(CHECKIN_STORAGE_KEY, JSON.stringify(savedCheckIns));
  }, [savedCheckIns]);

  const updateField = (field, value) => {
    setForm((current) => ({ ...current, [field]: value }));
  };

  const saveCheckIn = (event) => {
    event.preventDefault();

    const entry = {
      ...form,
      id: Date.now(),
      createdAt: new Date().toISOString(),
    };

    setSavedCheckIns((current) => [entry, ...current].slice(0, 8));
    onNotice("Check-in saved privately in this browser.");
  };

  const clearCheckIns = () => {
    setSavedCheckIns([]);
    onNotice("Saved check-ins were removed from this browser.");
  };

  return (
    <section className="rw-section rw-checkin-section" aria-labelledby="rw-checkin-title">
      <div className="rw-checkin-intro">
        <div>
          <span className="rw-eyebrow">Optional weekly reflection</span>
          <h2 id="rw-checkin-title">Notice the pattern, not just the day.</h2>
          <p>
            Exercises are never saved. This reflection is stored only in this browser when you explicitly choose to save it.
          </p>
        </div>

        <button
          type="button"
          className="rw-primary-button"
          onClick={() => setIsOpen((current) => !current)}
          aria-expanded={isOpen}
        >
          {isOpen ? "Close check-in" : "Open weekly check-in"}
        </button>
      </div>

      {isOpen && (
        <div className="rw-checkin-layout">
          <form className="rw-checkin-form" onSubmit={saveCheckIn}>
            <fieldset>
              <legend>How has this week felt overall?</legend>
              <div className="rw-mood-grid">
                {MOODS.map((mood) => (
                  <label
                    key={mood.value}
                    className={form.mood === mood.value ? "is-selected" : ""}
                  >
                    <input
                      type="radio"
                      name="mood"
                      value={mood.value}
                      checked={form.mood === mood.value}
                      onChange={(event) => updateField("mood", event.target.value)}
                      required
                    />
                    <span aria-hidden="true">{mood.emoji}</span>
                    <strong>{mood.label}</strong>
                  </label>
                ))}
              </div>
            </fieldset>

            <label className="rw-form-field">
              <span>Stress level: {form.stress}/10</span>
              <input
                type="range"
                min="0"
                max="10"
                value={form.stress}
                onChange={(event) => updateField("stress", Number(event.target.value))}
              />
            </label>

            <label className="rw-form-field">
              <span>What went well?</span>
              <textarea
                rows="3"
                value={form.wentWell}
                onChange={(event) => updateField("wentWell", event.target.value)}
              />
            </label>

            <label className="rw-form-field">
              <span>What felt challenging?</span>
              <textarea
                rows="3"
                value={form.challenge}
                onChange={(event) => updateField("challenge", event.target.value)}
              />
            </label>

            <label className="rw-form-field">
              <span>What is one small next step?</span>
              <input
                type="text"
                value={form.nextStep}
                onChange={(event) => updateField("nextStep", event.target.value)}
              />
            </label>

            <label className="rw-form-field">
              <span>Who did you connect with?</span>
              <input
                type="text"
                value={form.connection}
                onChange={(event) => updateField("connection", event.target.value)}
              />
            </label>

            <button type="submit" className="rw-primary-button">
              Save privately on this device
            </button>
          </form>

          <aside className="rw-checkin-history">
            <div className="rw-checkin-history-head">
              <h3>Recent check-ins</h3>
              {savedCheckIns.length > 0 && (
                <button type="button" onClick={clearCheckIns}>
                  Clear all
                </button>
              )}
            </div>

            {savedCheckIns.length === 0 ? (
              <p>No check-ins saved on this browser.</p>
            ) : (
              savedCheckIns.slice(0, 4).map((entry) => (
                <article key={entry.id}>
                  <div>
                    <strong>
                      {new Date(entry.createdAt).toLocaleDateString(undefined, {
                        month: "short",
                        day: "numeric",
                        year: "numeric",
                      })}
                    </strong>
                    <span>Stress {entry.stress}/10</span>
                  </div>
                  <p>{entry.nextStep || "No next step recorded."}</p>
                </article>
              ))
            )}

            <small>
              Anyone using this browser may be able to see saved entries. Do not save anything you would not want stored on this device.
            </small>
          </aside>
        </div>
      )}
    </section>
  );
}

function PrintableResources() {
  return (
    <section
      className="rw-section rw-printables-section"
      aria-labelledby="rw-printables-title"
    >
      <div className="rw-section-heading rw-section-heading--split">
        <div>
          <span className="rw-eyebrow">Print-ready resources</span>
          <h2 id="rw-printables-title">Take the toolkit into the community.</h2>
          <p>
            Download co-branded, standard letter-size materials for individual use,
            community distribution, and facilitated Wellness on Wheels programming.
          </p>
        </div>
        <div className="rw-printable-partner">
          <img
            src="/assets/images/wellness-on-wheels-logo.png"
            alt="Wellness on Wheels"
          />
          <span>Prepared for Wellness on Wheels</span>
        </div>
      </div>

      <div className="rw-printable-grid">
        {RURAL_PRINTABLE_RESOURCES.map((resource) => (
          <article
            key={resource.id}
            className={`rw-printable-card rw-printable-card--${resource.accent}`}
          >
            <div className="rw-printable-preview" aria-hidden="true">
              <span>{resource.pages.split(" ")[0]}</span>
              <small>pages</small>
            </div>

            <div className="rw-printable-copy">
              <span className="rw-resource-category">{resource.audience}</span>
              <h3>{resource.title}</h3>
              <p>{resource.description}</p>
              <div className="rw-printable-meta">
                <span>{resource.pages}</span>
                <span>8.5 × 11 in</span>
                <span>PDF</span>
              </div>
            </div>

            <div className="rw-printable-actions">
              <a href={resource.href} target="_blank" rel="noreferrer">
                Preview PDF
              </a>
              <a href={resource.href} download>
                Download
              </a>
            </div>
          </article>
        ))}
      </div>
    </section>
  );
}

function ToolkitLibrary() {
  return (
    <section className="rw-section rw-library-section" aria-labelledby="rw-library-title">
      <div className="rw-section-heading rw-section-heading--split">
        <div>
          <span className="rw-eyebrow">Full rural wellness guide</span>
          <h2 id="rw-library-title">Go deeper when you have time.</h2>
          <p>
            Open only the sections that are relevant. The immediate tools above remain the main experience.
          </p>
        </div>
        <button type="button" className="rw-secondary-button" onClick={() => window.print()}>
          Print guide
        </button>
      </div>

      <div className="rw-library-list">
        {RURAL_LIBRARY_SECTIONS.map((section) => (
          <details key={section.id} className="rw-library-item">
            <summary>
              <span>
                <strong>{section.title}</strong>
                <small>{section.summary}</small>
              </span>
              <span className="rw-library-plus" aria-hidden="true">+</span>
            </summary>

            <div className="rw-library-content">
              {section.bullets && (
                <ul>
                  {section.bullets.map((bullet) => (
                    <li key={bullet}>{bullet}</li>
                  ))}
                </ul>
              )}

              {section.groups && (
                <div className="rw-library-groups">
                  {section.groups.map((group) => (
                    <article key={group.label}>
                      <h3>{group.label}</h3>
                      <ul>
                        {group.items.map((item) => (
                          <li key={item}>{item}</li>
                        ))}
                      </ul>
                    </article>
                  ))}
                </div>
              )}
            </div>
          </details>
        ))}
      </div>
    </section>
  );
}

export default function RuralWellnessToolkit({
  communityName = "South Eastern New Brunswick",
  partnerName = "Wellness on Wheels",
  localResources = [],
}) {
  const [focus, setFocus] = useState(null);
  const [notice, setNotice] = useState("");
  const exerciseRef = useRef(null);

  const current = focus ? RURAL_TOOLKIT_BY_FOCUS[focus] : null;
  const resources = useMemo(
    () => [...RURAL_SUPPORT_RESOURCES, ...localResources],
    [localResources]
  );

  useEffect(() => {
    if (!notice) return undefined;

    const timer = window.setTimeout(() => setNotice(""), 3200);
    return () => window.clearTimeout(timer);
  }, [notice]);

  const selectFocus = (id) => {
    setFocus(id);

    window.setTimeout(() => {
      exerciseRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 80);
  };

  const renderTool = (tool, index) => {
    if (tool.type === "grounding") {
      return (
        <div className="rw-exercise-shell" key={`${tool.type}-${index}`}>
          <HelprrGroundingOrb
            title={tool.title}
            subtitle={tool.subtitle}
            mode={current.orbMode}
          />
        </div>
      );
    }

    if (tool.type === "body") {
      return (
        <div className="rw-exercise-shell" key={`${tool.type}-${index}`}>
          <HelprrBodyExercise
            title={tool.title}
            intro={tool.intro}
            steps={tool.steps}
            endNote={tool.endNote}
            mode={tool.mode}
          />
        </div>
      );
    }

    if (tool.type === "sensory") {
      return (
        <div className="rw-exercise-shell" key={`${tool.type}-${index}`}>
          <HelprrSensoryGrounding title={tool.title} subtitle={tool.subtitle} />
        </div>
      );
    }

    if (tool.type === "reflection") {
      return <ReflectionCard key={`${tool.type}-${index}`} {...tool} />;
    }

    if (tool.type === "connection") {
      return (
        <ConnectionCard
          key={`${tool.type}-${index}`}
          {...tool}
          onNotice={setNotice}
        />
      );
    }

    if (tool.type === "conversation") {
      return <ConversationCard key={`${tool.type}-${index}`} {...tool} />;
    }

    if (tool.type === "safety") {
      return <SafetyCard key={`${tool.type}-${index}`} {...tool} />;
    }

    if (tool.type === "resources") {
      return <ResourceDirectory key={`${tool.type}-${index}`} resources={resources} />;
    }

    return null;
  };

  return (
    <>
      <OuterHeader />

      <main className="rw-page">
        <section className="rw-hero">
          <div className="rw-hero-copy">
            <div className="rw-partner-lockup">
              <div className="rw-partner-logos" aria-label={`Helprr and ${partnerName}`}>
                <span className="rw-helprr-wordmark">helprr.</span>
                <span className="rw-partner-times" aria-hidden="true">×</span>
                <span className="rw-partner-logo-tile">
                  <img
                    src="/assets/images/wellness-on-wheels-logo.png"
                    alt={partnerName}
                  />
                </span>
              </div>
              <p>
                Built with <strong>{partnerName}</strong> for {communityName}
              </p>
            </div>
            <h1>
              Rooted in community.
              <br />
              Connected by care.
            </h1>
            <p className="rw-hero-sub">
              Practical, shame-free tools for stress, isolation, long winters, physical work, family life, and finding support from a distance.
            </p>
            <div className="rw-trust-row">
              {/* <span>No diagnosis</span>
              <span>No account required</span>
              <span>Exercises are not saved</span> */}
            </div>
          </div>

          <aside className="rw-hero-note">
            <span className="rw-hero-note-mark" aria-hidden="true">h</span>
            <div>
              <span className="rw-eyebrow">Start with what you need</span>
              <h2>You do not have to read everything.</h2>
              <p>
                Choose the closest match below. Each path begins with a short interactive exercise and ends with one practical next step.
              </p>
            </div>
          </aside>
        </section>

        <section className="rw-urgent-strip" aria-label="Immediate support information">
          <div>
            <strong>Need immediate support?</strong>
            <span>Call or text 9-8-8 in Canada. Call 9-1-1 for immediate danger or a medical emergency.</span>
          </div>
          <div className="rw-urgent-actions">
            <a href="tel:988">Call 9-8-8</a>
            <a href="sms:988">Text 9-8-8</a>
          </div>
        </section>

        <section className="rw-section rw-choice" aria-labelledby="rw-choice-title">
          <div className="rw-section-heading">
            <span className="rw-eyebrow">Start here</span>
            <h2 id="rw-choice-title">What would help most today?</h2>
            <p>
              Choose one. You can change paths at any time.
            </p>
          </div>

          <div className="rw-options">
            {RURAL_SUPPORT_OPTIONS.map((option) => (
              <button
                type="button"
                key={option.id}
                className={`rw-option ${focus === option.id ? "is-active" : ""}`}
                onClick={() => selectFocus(option.id)}
                aria-pressed={focus === option.id}
              >
                <span className="rw-option-icon" aria-hidden="true">{option.icon}</span>
                <span className="rw-option-copy">
                  <small>{option.eyebrow}</small>
                  <strong>{option.title}</strong>
                  <span>{option.description}</span>
                </span>
                <span className="rw-option-arrow" aria-hidden="true">→</span>
              </button>
            ))}
          </div>
        </section>

        {current && (
          <section className="rw-section rw-journey" ref={exerciseRef} aria-live="polite">
            <div className="rw-journey-heading">
              <button type="button" className="rw-change-path" onClick={() => setFocus(null)}>
                ← Change path
              </button>
              <span className="rw-eyebrow">Your guided path</span>
              <h2>{current.title}</h2>
              <p>{current.intro}</p>
            </div>

            <div className="rw-tool-stack">
              {current.tools.map((tool, index) => renderTool(tool, index))}
            </div>
          </section>
        )}

        <BodyBattery />
        <WeeklyCheckIn onNotice={setNotice} />
        <PrintableResources />
        <ToolkitLibrary />

        {focus !== "resources" && (
          <section className="rw-section rw-resources-section" aria-labelledby="rw-resources-title">
            <div className="rw-section-heading">
              <span className="rw-eyebrow">Canada-wide starting points</span>
              <h2 id="rw-resources-title">Find support beyond the toolkit.</h2>
              <p>
                Helprr is not a crisis service. These services can provide immediate or community-specific support.
              </p>
            </div>
            <ResourceDirectory resources={resources} compact />
            <button
              type="button"
              className="rw-secondary-button rw-view-resources"
              onClick={() => selectFocus("resources")}
            >
              View all support resources
            </button>
          </section>
        )}

        <section className="rw-closing-note">
          <span aria-hidden="true">✦</span>
          <div>
            <h2>One useful step is enough.</h2>
            <p>
              This toolkit supports awareness and early action. It does not replace professional mental-health or medical care.
            </p>
          </div>
        </section>

        {notice && (
          <div className="rw-toast" role="status">
            {notice}
          </div>
        )}
      </main>

      <Footer />
    </>
  );
}
